import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";

interface TripStopActionsProps {
  /** Named in the trigger's label so a screen reader hears whose menu it is. */
  readonly title: string;
  readonly hasNote: boolean;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
  readonly onEditTime?: () => void;
  readonly onEditNote?: () => void;
  readonly onMoveUp: () => void;
  readonly onMoveDown: () => void;
  readonly onRemove: () => void;
}

/**
 * One ⋯ per stop: Edit time, Add note, Move up, Move down, Remove.
 *
 * It replaces four 24px icons that were hard to hit on a phone and easy to hit
 * by mistake (Remove sat next to Move down). The menu opens in the flow of the
 * page, under its stop, rather than floating over it: a floating menu at the
 * bottom of the sheet slid under the bottom bar, and one in the flow just
 * pushes the next stop down and is scrolled clear of the bar when it opens.
 *
 * Keyboard: Enter, Space or an arrow opens it with focus on the first item;
 * arrows cycle, Home and End jump to the ends, Escape closes and returns focus
 * to the ⋯, Tab closes and carries on down the page.
 */
export function TripStopActions({
  title,
  hasNote,
  canMoveUp,
  canMoveDown,
  onEditTime,
  onEditNote,
  onMoveUp,
  onMoveDown,
  onRemove,
}: TripStopActionsProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  const items = () =>
    menuRef.current
      ? Array.from(menuRef.current.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"))
      : [];

  useEffect(() => {
    if (!open) return;
    items()[0]?.focus();
    // Clear of the bottom bar: the menu's scroll margin leaves room for it.
    menuRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [open]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const onMenuKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const list = items();
    if (list.length === 0) return;
    const i = list.indexOf(document.activeElement as HTMLButtonElement);
    const focus = (n: number) => {
      e.preventDefault();
      list[(n + list.length) % list.length]?.focus();
    };
    switch (e.key) {
      case "ArrowDown":
        focus(i + 1);
        break;
      case "ArrowUp":
        focus(i - 1);
        break;
      case "Home":
        focus(0);
        break;
      case "End":
        focus(list.length - 1);
        break;
      case "Escape":
        e.preventDefault();
        close(true);
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const run = (action: () => void, refocus = false) => () => {
    close(refocus);
    action();
  };

  return (
    <div className="mj-trip-stop-actions" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`mj-trip-stop-actions__trigger${open ? " is-open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("trip.stop_actions", { title })}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <Icon name="ri-more-fill" aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={t("trip.stop_actions", { title })}
          className="mj-trip-stop-actions__menu mj-fade-in"
          onKeyDown={onMenuKeyDown}
        >
          {onEditTime && (
            <button type="button" role="menuitem" className="mj-trip-stop-actions__item" onClick={run(onEditTime)}>
              <Icon name="ri-time-line" aria-hidden="true" />
              {t("trip.time.edit")}
            </button>
          )}
          {onEditNote && (
            <button type="button" role="menuitem" className="mj-trip-stop-actions__item" onClick={run(onEditNote)}>
              <Icon name="ri-sticky-note-line" aria-hidden="true" />
              {hasNote ? t("trip.edit_note") : t("trip.add_note")}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="mj-trip-stop-actions__item"
            disabled={!canMoveUp}
            onClick={run(onMoveUp, true)}
          >
            <Icon name="ri-arrow-up-line" aria-hidden="true" />
            {t("trip.timeline.move_up")}
          </button>
          <button
            type="button"
            role="menuitem"
            className="mj-trip-stop-actions__item"
            disabled={!canMoveDown}
            onClick={run(onMoveDown, true)}
          >
            <Icon name="ri-arrow-down-line" aria-hidden="true" />
            {t("trip.timeline.move_down")}
          </button>
          <button
            type="button"
            role="menuitem"
            className="mj-trip-stop-actions__item mj-trip-stop-actions__item--danger"
            onClick={run(onRemove)}
          >
            <Icon name="ri-delete-bin-line" aria-hidden="true" />
            {t("trip.remove")}
          </button>
        </div>
      )}
    </div>
  );
}
